/**
 * Partner Service - Frontend API calls for partner operations
 */

import api from "@/shared/api";

/**
 * Get partners with optional filters
 * @param {Object} filters - Filter criteria
 * @param {boolean} filters.isActive - Active status
 * @param {boolean} filters.phoneIsWhatsApp - WhatsApp-ready phone filter
 * @returns {Promise<Object>} Partners list
 */
const buildPartnerQueryParams = (filters = {}) => {
  const params = {};
  if (filters.isActive === true || filters.isActive === false) {
    params.isActive = filters.isActive;
  }
  if (filters.phoneIsWhatsApp === true || filters.phoneIsWhatsApp === false) {
    params.phoneIsWhatsApp = filters.phoneIsWhatsApp;
  }
  return params;
};

export const getPartners = async (filters = {}) => {
  try {
    const response = await api.get("/partners", {
      params: buildPartnerQueryParams(filters),
    });
    return response;
  } catch (error) {
    console.error("Error fetching partners:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch partners",
    };
  }
};

/**
 * Get partner by ID
 * @param {string} partnerId - Partner ID
 * @returns {Promise<Object>} Partner details
 */
export const getPartner = async (partnerId) => {
  try {
    const response = await api.get(`/partners/${partnerId}`);
    return response;
  } catch (error) {
    console.error("Error fetching partner:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch partner",
    };
  }
};

/**
 * Create partner
 * @param {Object} partnerData - Partner data
 * @param {string} partnerData.name - Partner company name
 * @param {string} partnerData.contactPerson - Contact person name
 * @param {string} partnerData.phone - Contact phone
 * @param {string} [partnerData.email] - Contact email
 * @param {string} [partnerData.address] - Partner address
 * @param {Object} [partnerData.contractDetails] - Contract details
 * @returns {Promise<Object>} Creation result
 */
export const createPartner = async (partnerData) => {
  try {
    const response = await api.post("/partners", partnerData);
    return response;
  } catch (error) {
    console.error("Error creating partner:", error);
    return {
      success: false,
      error: error.message || "Failed to create partner",
    };
  }
};

/**
 * Update partner
 * @param {string} partnerId - Partner ID
 * @param {Object} updates - Update data
 * @returns {Promise<Object>} Update result
 */
export const updatePartner = async (partnerId, updates) => {
  try {
    const response = await api.put(`/partners/${partnerId}`, updates);
    return response;
  } catch (error) {
    console.error("Error updating partner:", error);
    return {
      success: false,
      error: error.message || "Failed to update partner",
    };
  }
};

/**
 * Preview upcoming partner jobs and available share channels.
 * @param {string} partnerId
 * @param {Object} [filters]
 * @returns {Promise<Object>}
 */
export const getPartnerJobsSchedulePreview = async (partnerId, filters = {}) => {
  try {
    const response = await api.get(`/partners/${partnerId}/jobs/schedule-preview`, {
      params: filters,
    });
    return response;
  } catch (error) {
    console.error("Error fetching partner jobs schedule preview:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch schedule preview",
    };
  }
};

/**
 * Share partner job schedule via all available channels.
 * @param {string} partnerId
 * @param {Object} [filters]
 * @returns {Promise<Object>}
 */
export const sharePartnerJobsSchedule = async (partnerId, filters = {}) => {
  try {
    const response = await api.post(`/partners/${partnerId}/jobs/share`, filters);
    return response;
  } catch (error) {
    console.error("Error sharing partner jobs schedule:", error);
    return {
      success: false,
      error: error.message || "Failed to share schedule",
    };
  }
};

/**
 * Trigger browser download from a binary API response.
 * @param {Response} response
 * @param {string} fallbackFilename
 */
const downloadBlobFromResponse = async (response, fallbackFilename) => {
  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = fallbackFilename;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) {
      filename = match[1];
    }
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
};

/**
 * Download partner upcoming jobs schedule as CSV.
 * @param {string} partnerId
 * @param {Object} [filters]
 */
export const downloadPartnerJobsScheduleCsv = async (
  partnerId,
  filters = {}
) => {
  try {
    const response = await api.binary(
      `/partners/${partnerId}/jobs/schedule.csv`,
      filters,
      "text/csv"
    );
    await downloadBlobFromResponse(
      response,
      `Jobs_Schedule_${partnerId}.csv`
    );
    return { success: true };
  } catch (error) {
    console.error("Error downloading partner jobs CSV:", error);
    return {
      success: false,
      error: error.message || "Failed to download CSV",
    };
  }
};

/**
 * Download partner upcoming jobs schedule as PDF.
 * @param {string} partnerId
 * @param {Object} [filters]
 */
export const downloadPartnerAgreementPdf = async (partnerId) => {
  try {
    const response = await api.binary(
      `/partners/${partnerId}/agreement.pdf`,
      {},
      "application/pdf"
    );
    await downloadBlobFromResponse(
      response,
      `Bubakii_Provider_Agreement_${partnerId}.pdf`
    );
    return { success: true };
  } catch (error) {
    console.error("Error downloading provider agreement PDF", error);
    return {
      success: false,
      error: error.message || "Failed to download agreement PDF",
    };
  }
};

export const downloadPartnerJobsSchedulePdf = async (
  partnerId,
  filters = {}
) => {
  try {
    const response = await api.binary(
      `/partners/${partnerId}/jobs/schedule.pdf`,
      filters,
      "application/pdf"
    );
    await downloadBlobFromResponse(response, `Job_Pack_${partnerId}.pdf`);
    return { success: true };
  } catch (error) {
    console.error("Error downloading partner jobs PDF:", error);
    return {
      success: false,
      error: error.message || "Failed to download PDF",
    };
  }
};

